import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { useAuthStore } from '@/store/authStore';
import { notificationAPI } from '@/services/api';
import { X, Send, Minus } from 'lucide-react';

const APPLICANT_QUICK = ['Build my resume', 'Improve my profile', 'What skills should I learn?'];
const RECRUITER_QUICK = ['Find top candidates', 'Screening best practices', 'How to write better JDs'];

/* Markdown renderer mapping — defined outside component to avoid re-creating on each render */
const MD_COMPONENTS = {
  p: (props) => <p className="my-1 first:mt-0 last:mb-0 leading-relaxed" {...props} />,
  strong: (props) => <strong className="font-semibold text-slate-900 dark:text-white" {...props} />,
  em: (props) => <em className="italic text-slate-800 dark:text-zinc-100" {...props} />,
  ul: (props) => <ul className="list-disc pl-4 my-1.5 space-y-0.5" {...props} />,
  ol: (props) => <ol className="list-decimal pl-4 my-1.5 space-y-0.5" {...props} />,
  li: (props) => <li className="leading-relaxed" {...props} />,
  code: ({inline, ...props}) => inline
    ? <code className="px-1 py-0.5 rounded bg-slate-50 dark:bg-zinc-900 text-emerald-700 dark:text-emerald-300 text-[12px] font-mono" {...props} />
    : <code className="block p-2 my-1.5 rounded bg-slate-50 dark:bg-zinc-900 text-emerald-700 dark:text-emerald-300 text-[12px] font-mono overflow-x-auto whitespace-pre-wrap" {...props} />,
  a: (props) => <a className="text-blue-600 dark:text-blue-400 underline hover:text-blue-700 dark:hover:text-blue-300" target="_blank" rel="noreferrer" {...props} />,
  h1: (props) => <h1 className="text-slate-900 dark:text-white font-semibold text-sm mt-2 mb-1" {...props} />,
  h2: (props) => <h2 className="text-slate-900 dark:text-white font-semibold text-sm mt-2 mb-1" {...props} />,
  h3: (props) => <h3 className="text-slate-900 dark:text-white font-semibold text-[13px] mt-2 mb-1" {...props} />,
  blockquote: (props) => <blockquote className="border-l-2 border-slate-300 dark:border-zinc-600 pl-2 my-1 text-slate-600 dark:text-zinc-300 italic" {...props} />,
  hr: () => <hr className="border-slate-300 dark:border-zinc-700 my-2" />,
};

/* Blue Jay SVG — bright orange beak for visibility */
function JayBirdSVG({ size = 44, flip = false }) {
  return (
    <svg width={size} height={Math.round(size * 0.82)} viewBox="0 0 60 50" className="drop-shadow-md" style={flip ? { transform: 'scaleX(-1)' } : {}}>
      <path d="M8 30 L-2 36 Q0 30 2 28Z" fill="#1E5FB3"/><path d="M6 28 L-4 32 Q-1 27 3 25Z" fill="#2970CC"/><path d="M7 32 L0 40 Q3 35 5 31Z" fill="#164D94"/>
      <ellipse cx="24" cy="30" rx="17" ry="14" fill="#4A90D9"/>
      <path d="M12 24 Q8 18 10 14 Q14 18 18 22 Q22 26 20 30Z" fill="#1E5FB3"/>
      <path d="M14 22 L11 16" stroke="#BFDBFE" strokeWidth="0.8" opacity="0.5"/><path d="M16 24 L14 19" stroke="#BFDBFE" strokeWidth="0.8" opacity="0.5"/>
      <ellipse cx="26" cy="35" rx="11" ry="8" fill="#E8EFF8"/>
      <circle cx="40" cy="18" r="12" fill="#5BA0E6"/>
      <path d="M38 6 L34 -2 L37 5 L40 -1 L39 6 L44 0 L41 7" fill="#2970CC"/>
      <path d="M30 24 Q34 28 40 28 Q46 28 50 24 Q48 26 44 27 Q40 30 36 27 Q32 26 30 24Z" fill="#1E293B"/>
      <path d="M34 16 Q38 14 42 16 Q44 18 42 20 Q38 18 34 20 Q32 18 34 16Z" fill="#1E293B"/>
      <circle cx="42" cy="16" r="3" fill="white"/><circle cx="42.5" cy="15.8" r="1.6" fill="#1E293B"/><circle cx="43" cy="15.2" r="0.5" fill="white"/>
      {/* Bright orange beak */}
      <path d="M52 18 L59 17 L52 20Z" fill="#F59E0B"/>
      <path d="M52 18.5 L57 17.8 L52 19.5Z" fill="#FBBF24"/>
      <line x1="22" y1="42" x2="20" y2="48" stroke="#6B7280" strokeWidth="1.5"/><line x1="28" y1="42" x2="30" y2="48" stroke="#6B7280" strokeWidth="1.5"/>
    </svg>
  );
}

function JayBirdMini() {
  return (
    <svg width="18" height="15" viewBox="0 0 60 50" className="flex-shrink-0 mt-1">
      <ellipse cx="24" cy="30" rx="17" ry="14" fill="#4A90D9"/><circle cx="40" cy="18" r="12" fill="#5BA0E6"/>
      <path d="M38 6 L36 0 L40 -1 L41 7" fill="#2970CC"/><circle cx="42" cy="16" r="2" fill="white"/><circle cx="42.5" cy="15.8" r="1" fill="#1E293B"/>
      <path d="M52 18 L58 17 L52 20Z" fill="#F59E0B"/><ellipse cx="26" cy="35" rx="11" ry="8" fill="#E8EFF8"/>
    </svg>
  );
}

function NestSVG() {
  return (
    <svg width="70" height="40" viewBox="0 0 70 40" className="drop-shadow-md">
      <path d="M5 30 Q15 15 35 18 Q55 15 65 30" fill="none" stroke="#8B6914" strokeWidth="3.5" strokeLinecap="round"/>
      <path d="M8 28 Q20 20 35 22 Q50 20 62 28" fill="none" stroke="#A67C1A" strokeWidth="2.5" strokeLinecap="round"/>
      <ellipse cx="35" cy="30" rx="28" ry="8" fill="#6B4E14" opacity="0.6"/>
      <path d="M10 32 Q18 25 28 28" fill="none" stroke="#5C4312" strokeWidth="1.5"/><path d="M42 28 Q52 25 60 32" fill="none" stroke="#5C4312" strokeWidth="1.5"/>
      <ellipse cx="25" cy="26" rx="3" ry="1.5" fill="#22c55e" opacity="0.5" transform="rotate(-15,25,26)"/>
      <ellipse cx="45" cy="26" rx="2.5" ry="1.5" fill="#4ade80" opacity="0.4" transform="rotate(10,45,26)"/>
    </svg>
  );
}

/* Flying bird with orange beak for entry animation */
function FlyingBird({ flipped = false }) {
  const inner = (
    <svg width="62" height="50" viewBox="0 0 60 50" className="drop-shadow-lg">
      <ellipse cx="24" cy="30" rx="17" ry="14" fill="#4A90D9"/>
      <path d="M12 24 Q8 18 10 14 Q14 18 18 22 Q22 26 20 30Z" fill="#1E5FB3" style={{animation:'wingFlap 0.16s ease-in-out infinite alternate'}}/>
      <ellipse cx="26" cy="35" rx="11" ry="8" fill="#E8EFF8"/>
      <circle cx="40" cy="18" r="12" fill="#5BA0E6"/>
      <path d="M38 6 L34 -2 L37 5 L40 -1 L39 6 L44 0 L41 7" fill="#2970CC"/>
      <path d="M30 24 Q40 30 50 24" stroke="#1E293B" strokeWidth="2" fill="none"/>
      <circle cx="42" cy="16" r="3" fill="white"/><circle cx="42.5" cy="15.8" r="1.6" fill="#1E293B"/>
      <path d="M52 18 L59 17 L52 20Z" fill="#F59E0B"/>
      <path d="M8 30 L-2 36 Q0 30 2 28Z" fill="#1E5FB3"/>
    </svg>
  );
  return flipped ? <div style={{transform:'scaleX(-1)'}}>{inner}</div> : inner;
}

const IDLE_ACTIONS = ['hop', 'headtilt', 'wingwave', 'flip', 'peck', 'fluff', 'stretch', 'look', 'chirp', 'nap', 'tail', 'blink'];
const THOUGHTS = ['Need help with your profile?', 'I can review your skills!', 'Ask me about interview tips', 'Want to find skill gaps?', 'Click me for career advice!'];
const RECRUITER_THOUGHTS = ['Need help writing a JD?', 'I can find candidates!', 'Ask me screening tips', 'Want analytics insights?', 'Click me for hiring help!'];

export default function JayFloatingBot() {
  const { user } = useAuthStore();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [unread, setUnread] = useState(0);
  const [typing, setTyping] = useState(false);

  const [showEntryFly, setShowEntryFly] = useState(false);
  const [birdOnBranch, setBirdOnBranch] = useState(false);
  const [idleAction, setIdleAction] = useState('');
  const [facingLeft, setFacingLeft] = useState(false);
  const [thoughtBubble, setThoughtBubble] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const scrollRef = useRef(null);

  const isRecruiter = user?.role === 'recruiter';
  const quickActions = isRecruiter ? RECRUITER_QUICK : APPLICANT_QUICK;

  // ── Sidebar events — just fade Jay in/out to avoid overlay glitches ──
  useEffect(() => {
    const onOpen = () => { setSidebarOpen(true); setOpen(false); };
    const onClose = () => { setSidebarOpen(false); };
    window.addEventListener('jay:sidebar-open', onOpen);
    window.addEventListener('jay:sidebar-close', onClose);
    return () => {
      window.removeEventListener('jay:sidebar-open', onOpen);
      window.removeEventListener('jay:sidebar-close', onClose);
    };
  }, []);

  // ── Safety net: route changes should always restore Jay (in case a sidebar-close event was missed) ──
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // ── Logout ── fade out; entry-fly will handle next mount
  useEffect(() => {
    const onLogout = () => {
      setBirdOnBranch(false);
      setOpen(false);
      entryDoneRef.current = false;
      try { window.sessionStorage.removeItem('jay_entry_done'); } catch (e) { /* private mode */ }
    };
    window.addEventListener('jay:logout', onLogout);
    return () => window.removeEventListener('jay:logout', onLogout);
  }, []);

  // ── Entry: fly-in once per login-session, guaranteed to land Jay on the branch ──
  // Persist the "played" flag in sessionStorage so the fly-in does NOT replay on
  // page refresh or hard route navigation within the same tab.
  const entryDoneRef = useRef(typeof window !== 'undefined' && window.sessionStorage.getItem('jay_entry_done') === '1');
  useEffect(() => {
    if (!user || user.role === 'admin') {
      setBirdOnBranch(false);
      setShowEntryFly(false);
      return;
    }
    // If we've already played the fly-in this session, just show Jay on branch.
    if (entryDoneRef.current) {
      setBirdOnBranch(true);
      setShowEntryFly(false);
      return;
    }
    // Play fly-in. Mark done IMMEDIATELY so any re-run of this effect
    // (auth store re-emits with a fresh user reference) does NOT restart
    // or cancel the animation — it just jumps to the "landed" branch above.
    entryDoneRef.current = true;
    try { window.sessionStorage.setItem('jay_entry_done', '1'); } catch (e) { /* private mode */ }
    setShowEntryFly(true);
    setBirdOnBranch(false);
    const land = setTimeout(() => {
      setBirdOnBranch(true);
      setShowEntryFly(false);
    }, 3200);
    return () => clearTimeout(land);
  }, [user?.id, user?.role]);

  // ── Idle actions every ~15s, first at 3s ──
  useEffect(() => {
    if (!birdOnBranch || !user || user.role === 'admin') return;
    let tid;
    let isFirst = true;
    const next = () => {
      const delay = isFirst ? 3000 : (13000 + Math.random() * 4000);
      isFirst = false;
      tid = setTimeout(() => {
        const action = IDLE_ACTIONS[Math.floor(Math.random() * IDLE_ACTIONS.length)];
        setIdleAction(action);
        if (action === 'flip') setFacingLeft(prev => !prev);
        const dur = action === 'nap' ? 3000 : action === 'look' ? 2200 : action === 'stretch' ? 1800 : 1400;
        setTimeout(() => setIdleAction(''), dur);
        next();
      }, delay);
    };
    next();
    return () => clearTimeout(tid);
  }, [birdOnBranch, user]);

  // ── Thought bubbles every 20-35s ──
  useEffect(() => {
    if (!birdOnBranch || !user || user.role === 'admin' || open) return;
    let tid;
    const show = () => {
      tid = setTimeout(() => {
        const pool = isRecruiter ? RECRUITER_THOUGHTS : THOUGHTS;
        setThoughtBubble(pool[Math.floor(Math.random() * pool.length)]);
        setTimeout(() => setThoughtBubble(''), 5000);
        show();
      }, 20000 + Math.random() * 15000);
    };
    show();
    return () => clearTimeout(tid);
  }, [birdOnBranch, user, open, isRecruiter]);

  useEffect(() => {
    if (!user) return;
    notificationAPI.getUnreadCount().then(d => setUnread(d.unread_count)).catch(() => { /* noop */ });
  }, [user]);

  useEffect(() => {
    if (open && messages.length === 0) {
      const name = user?.email?.split('@')[0] || 'there';
      const txt = isRecruiter
        ? `Hey ${name}! I'm Jay, your AI recruitment assistant. Need help finding candidates, writing JDs, or screening tips?`
        : `Hey ${name}! I'm Jay, your AI career coach. I can help improve your profile, find skill gaps, or prep for interviews!`;
      setMessages([{ from: 'jay', text: txt }]);
    }
  }, [open, user, messages.length, isRecruiter]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, typing]);

  const handleSend = useCallback(async () => {
    if (!input.trim() || typing) return;
    const userMsg = input.trim();
    const newMessages = [...messages, { from: 'user', text: userMsg }];
    setMessages(newMessages);
    setInput('');
    setTyping(true);
    try {
      const history = newMessages.slice(-8).map(m => ({ from: m.from, text: m.text }));
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('auth_token');
      const resp = await fetch(`${API_URL}/api/jay/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ message: userMsg, history })
      }).then(r => r.json());
      const jayMsg = { from: 'jay', text: resp.response };
      if (resp.resume_url) {
        jayMsg.resumeUrl = resp.resume_url;
        jayMsg.resumeName = resp.resume_filename || 'Numuv_Resume.pdf';
      }
      if (resp.builder?.in_builder) {
        jayMsg.builderProgress = resp.builder.progress || {};
      }
      if (resp.profile_updated) {
        const updateLabels = { profile_updated: 'Profile updated!', recruiter_profile_updated: 'Recruiter profile updated!', job_updated: 'Job posting updated!' };
        jayMsg.profileUpdate = updateLabels[resp.update_type] || 'Profile updated!';
        // Dispatch event so pages can refresh their data
        window.dispatchEvent(new CustomEvent('jay:profile-updated', { detail: { type: resp.update_type, updates: resp.updates_applied } }));
      }
      if (resp.feedback_captured) {
        jayMsg.feedbackCaptured = true;
      }
      setMessages(prev => [...prev, jayMsg]);
    } catch {
      setMessages(prev => [...prev, { from: 'jay', text: "My feathers got ruffled! Try again in a sec." }]);
    }
    setTyping(false);
  }, [input, typing, messages]);

  if (!user || user.role === 'admin') return null;

  const idleClass = {
    hop: 'animate-[jayHop_0.5s_ease-in-out]',
    headtilt: 'animate-[jayTilt_1s_ease-in-out]',
    wingwave: 'animate-[jayWingWave_0.7s_ease-in-out_2]',
    peck: 'animate-[jayPeck_0.4s_ease-in-out_3]',
    fluff: 'animate-[jayFluff_0.6s_ease-in-out]',
    stretch: 'animate-[jayStretch_1.2s_ease-in-out]',
    look: 'animate-[jayLook_1.8s_ease-in-out]',
    chirp: 'animate-[jayChirp_0.5s_ease-in-out_3]',
    nap: 'animate-[jayNap_2.8s_ease-in-out]',
    tail: 'animate-[jayTail_0.6s_ease-in-out_2]',
    blink: 'animate-[jayBlink_0.4s_ease-in-out_2]',
    flip: '',
  }[idleAction] || '';

  return (
    <>
      {/* ══ ENTRY FLY — left to right ══ */}
      {showEntryFly && (
        <div className="fixed inset-0 z-[100] pointer-events-none overflow-hidden" data-testid="jay-entry-fly">
          {[...Array(12)].map((_, i) => (
            <div key={i} className="absolute rounded-full" style={{
              width:`${3+(i%3)}px`, height:`${3+(i%3)}px`,
              background:['#5BA0E6','#F59E0B','#BFDBFE','#22c55e'][i%4],
              left:'-10px', bottom:'55px', opacity:0,
              animation:`sparkleTrailLTR 3.2s ease-out ${i*0.15}s forwards`,
            }}/>
          ))}
          <div className="absolute" style={{bottom:'44px',left:'-80px',animation:'jayFlyEntryLTR 3.2s cubic-bezier(0.22,0.61,0.36,1) forwards'}}>
            <FlyingBird />
          </div>
        </div>
      )}

      {/* ══ JAY ON BRANCH — responsive bottom-right ══ */}
      {birdOnBranch && !sidebarOpen && (
        <div className="fixed z-[90]" style={{bottom:'12px',right:'16px',animation:'jayLandBounce 0.7s ease-out'}} data-testid="jay-on-branch">
          {/* Thought bubble */}
          {thoughtBubble && !open && (
            <div className="absolute -top-14 right-0 px-3 py-1.5 bg-white dark:bg-[#111114] border border-slate-300 dark:border-zinc-700/60 rounded-xl text-xs text-slate-600 dark:text-zinc-300 whitespace-nowrap shadow-lg"
                 style={{animation: 'thoughtPop 0.3s ease-out'}} data-testid="jay-thought-bubble">
              {thoughtBubble}
              <div className="absolute -bottom-1.5 right-6 w-3 h-3 bg-white dark:bg-[#111114] border-b border-r border-slate-300 dark:border-zinc-700/60 transform rotate-45" />
            </div>
          )}
          {/* Branch (mirrored for right side) */}
          <svg className="absolute -bottom-1 -right-3 w-28 h-12 pointer-events-none" viewBox="0 0 112 48" style={{transform:'scaleX(-1)'}}>
            <path d="M0 30 Q25 25 50 33 Q70 40 90 36 Q100 33 112 35" fill="none" stroke="#6B4226" strokeWidth="4" strokeLinecap="round"/>
            <ellipse cx="42" cy="24" rx="7" ry="4" fill="#22c55e" opacity="0.65" transform="rotate(-15,42,24)"/>
            <ellipse cx="62" cy="28" rx="6" ry="3.5" fill="#4ade80" opacity="0.55" transform="rotate(10,62,28)"/>
          </svg>
          <button onClick={() => { if (minimized) { setMinimized(false); setOpen(true); } else setOpen(!open); setUnread(0); setThoughtBubble(''); }}
            className={`relative w-14 h-14 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110 ${idleClass}`}
            data-testid="jay-bot-btn" style={{background:'transparent', animation: !idleAction ? 'jayBreathe 4s ease-in-out infinite' : undefined}}>
            {open ? <X className="w-5 h-5 text-slate-900 dark:text-white bg-slate-100 dark:bg-zinc-800 rounded-full p-1 shadow-lg" style={{width:28,height:28}} /> : (
              <div className="relative">
                <JayBirdSVG size={46} flip={facingLeft} />
                {unread > 0 && <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center px-1 font-bold shadow">{unread > 99 ? '99+' : unread}</span>}
              </div>
            )}
          </button>
        </div>
      )}

      {/* ══ CHAT PANEL — bottom-right ══ */}
      {open && !minimized && birdOnBranch && (
        <div className="fixed z-[90] w-[340px] sm:w-[400px] max-h-[520px] bg-white dark:bg-[#0e0e11] border border-slate-200 dark:border-zinc-800/80 rounded-2xl shadow-2xl flex flex-col overflow-hidden"
             style={{bottom:'80px',right:'16px'}} data-testid="jay-chat-panel">
          <div className="px-4 py-3 border-b border-slate-200 dark:border-zinc-800/50 flex items-center gap-3" style={{background:'linear-gradient(135deg,rgba(59,130,246,0.08) 0%,transparent 100%)'}}>
            <JayBirdSVG size={28} />
            <div className="flex-1"><p className="text-slate-900 dark:text-white font-semibold text-sm">Jay</p><p className="text-blue-600/70 dark:text-blue-400/60 text-[11px]">{isRecruiter ? 'AI Recruitment Assistant' : 'AI Career Coach'}</p></div>
            <button onClick={() => { setMinimized(true); setOpen(false); }} className="p-1 text-slate-500 dark:text-zinc-500 hover:text-slate-900 dark:hover:text-white rounded transition-colors" title="Minimize" data-testid="jay-minimize-btn"><Minus className="w-4 h-4" /></button>
            <button onClick={() => setOpen(false)} className="p-1 text-slate-500 dark:text-zinc-500 hover:text-slate-900 dark:hover:text-white rounded transition-colors" title="Close" data-testid="jay-close-btn"><X className="w-4 h-4" /></button>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-3 max-h-[350px]">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.from === 'user' ? 'justify-end' : 'justify-start'}`} style={{animation:'fadeSlideUp 0.3s ease-out'}}>
                {msg.from === 'jay' && <JayBirdMini />}
                <div className={`max-w-[82%] px-3.5 py-2.5 text-[13px] leading-relaxed ${msg.from === 'user' ? 'bg-blue-600 text-white rounded-2xl rounded-br-md' : 'bg-slate-100 dark:bg-zinc-800/70 text-slate-700 dark:text-zinc-200 rounded-2xl rounded-bl-md ml-1.5'}`}>
                  {msg.from === 'jay' ? (
                    <div className="jay-md" data-testid="jay-message-md">
                      <ReactMarkdown components={MD_COMPONENTS}>{msg.text || ''}</ReactMarkdown>
                    </div>
                  ) : (
                    <span className="whitespace-pre-wrap">{msg.text}</span>
                  )}
                  {msg.resumeUrl && (
                    <a href={msg.resumeUrl} target="_blank" rel="noopener noreferrer"
                       className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-[11.5px] font-semibold transition-colors"
                       data-testid="jay-resume-download">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 3v12m0 0l-4-4m4 4l4-4M4 21h16" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      Download {msg.resumeName}
                    </a>
                  )}
                  {msg.builderProgress?.total && (
                    <div className="mt-2 text-[10.5px] text-slate-500 dark:text-zinc-400" data-testid="jay-builder-progress">
                      Step {msg.builderProgress.index} of {msg.builderProgress.total} · profile {msg.builderProgress.completeness}% complete
                    </div>
                  )}
                  {msg.profileUpdate && (
                    <div className="mt-2 px-2 py-1.5 bg-emerald-500/15 border border-emerald-500/20 rounded-lg text-emerald-600 dark:text-emerald-400 text-[11px] flex items-center gap-1.5">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>
                      {msg.profileUpdate}
                    </div>
                  )}
                  {msg.feedbackCaptured && (
                    <div className="mt-2 px-2 py-1.5 bg-amber-500/15 border border-amber-500/25 rounded-lg text-amber-700 dark:text-amber-300 text-[11px] flex items-center gap-1.5" data-testid="jay-feedback-badge">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z"/><path d="M15 7v2a4 4 0 01-4 4H9.828l-1.766 1.767c.28.149.599.233.938.233h2l3 3v-3h2a2 2 0 002-2V9a2 2 0 00-2-2h-1z"/></svg>
                      Feedback logged for the Numuv team
                    </div>
                  )}
                </div>
              </div>
            ))}
            {typing && <div className="flex items-center gap-2 text-slate-500 dark:text-zinc-500 text-xs"><div className="flex gap-1"><span className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-bounce"/><span className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-bounce" style={{animationDelay:'150ms'}}/><span className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-bounce" style={{animationDelay:'300ms'}}/></div>Jay is thinking...</div>}
          </div>
          {messages.length <= 1 && <div className="px-3 pb-2 flex flex-wrap gap-1.5">{quickActions.map(q => <button key={q} onClick={() => setInput(q)} className="px-2.5 py-1 bg-slate-100 dark:bg-zinc-800/50 border border-slate-300 dark:border-zinc-700/50 rounded-full text-[11px] text-slate-500 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white hover:border-slate-300 dark:hover:border-zinc-600 transition-colors">{q}</button>)}</div>}
          <div className="px-3 py-2.5 border-t border-slate-200 dark:border-zinc-800/50">
            <form onSubmit={e => { e.preventDefault(); handleSend(); }} className="flex gap-2">
              <input value={input} onChange={e => setInput(e.target.value)} placeholder="Ask Jay anything..." className="flex-1 bg-slate-50 dark:bg-zinc-900/80 border border-slate-200 dark:border-zinc-800/60 rounded-xl px-3.5 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600 focus:outline-none focus:border-blue-500/40" data-testid="jay-chat-input" disabled={typing} />
              <button type="submit" disabled={typing||!input.trim()} className="p-2.5 bg-blue-600 rounded-xl text-white hover:bg-blue-500 disabled:opacity-30" data-testid="jay-send-btn"><Send className="w-4 h-4" /></button>
            </form>
          </div>
        </div>
      )}

      <style>{`
        @keyframes jayFlyEntryLTR {
          0%{transform:translate(0,0) rotate(-5deg);opacity:0}
          5%{opacity:1}
          15%{transform:translate(15vw,-50px) rotate(4deg)}
          35%{transform:translate(40vw,-40px) rotate(-3deg)}
          55%{transform:translate(65vw,-25px) rotate(2deg)}
          75%{transform:translate(85vw,-8px) rotate(-1deg)}
          90%{transform:translate(calc(100vw - 100px),-2px) rotate(0deg)}
          95%{transform:translate(calc(100vw - 95px),3px) rotate(0.5deg)}
          100%{transform:translate(calc(100vw - 98px),0px) rotate(0deg)}
        }
        @keyframes sparkleTrailLTR {
          0%{transform:translate(0,0) scale(0);opacity:0}
          10%{opacity:0.8;transform:translate(6vw,-20px) scale(1.1)}
          30%{opacity:0.5;transform:translate(25vw,-45px) scale(0.8)}
          55%{opacity:0.3;transform:translate(50vw,-20px) scale(0.5)}
          80%{opacity:0.1;transform:translate(75vw,-5px) scale(0.3)}
          100%{opacity:0;transform:translate(90vw,3px) scale(0)}
        }
        @keyframes jayHopToNest {
          0%{transform:translateY(0) scale(1);opacity:1}
          35%{transform:translateY(-25px) scale(0.92)}
          70%{transform:translateY(-8px) scale(0.82)}
          100%{transform:translateY(6px) scale(0.72);opacity:0.6}
        }
        @keyframes jayHopFromNest {
          0%{transform:translateY(6px) scale(0.72);opacity:0.6}
          30%{transform:translateY(-25px) scale(0.88);opacity:1}
          65%{transform:translateY(-5px) scale(1.04)}
          85%{transform:translateY(2px) scale(0.97)}
          100%{transform:translateY(0) scale(1);opacity:1}
        }
        @keyframes jayNestSettle{0%{transform:translate(-50%,-6px) scale(0.6);opacity:0}50%{transform:translate(-50%,2px) scale(1.06);opacity:1}75%{transform:translate(-50%,-1px) scale(0.96)}100%{transform:translate(-50%,0) scale(1)}}
        @keyframes jayLandBounce{0%{transform:scale(0.8) translateY(-10px);opacity:0}25%{opacity:1;transform:scale(1.1) translateY(-5px)}50%{transform:scale(0.95) translateY(3px)}70%{transform:scale(1.03) translateY(-1px)}100%{transform:scale(1) translateY(0)}}
        @keyframes wingFlap{0%{transform:rotate(0) scaleY(1)}100%{transform:rotate(-20deg) scaleY(0.6)}}
        @keyframes fadeIn{to{opacity:1}}
        @keyframes fadeSlideUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        @keyframes jayHop{0%,100%{transform:translateY(0)}25%{transform:translateY(-16px)}50%{transform:translateY(-5px)}75%{transform:translateY(-10px)}}
        @keyframes jayTilt{0%,100%{transform:rotate(0)}15%{transform:rotate(20deg)}40%{transform:rotate(-12deg)}60%{transform:rotate(8deg)}80%{transform:rotate(-4deg)}}
        @keyframes jayWingWave{0%,100%{transform:scaleX(1)}30%{transform:scaleX(1.18) scaleY(0.88)}60%{transform:scaleX(0.92) scaleY(1.08)}}
        @keyframes jayPeck{0%,100%{transform:translateY(0) rotate(0)}20%{transform:translateY(7px) rotate(14deg)}40%{transform:translateY(2px) rotate(5deg)}60%{transform:translateY(7px) rotate(14deg)}80%{transform:translateY(3px) rotate(6deg)}}
        @keyframes jayFluff{0%,100%{transform:scale(1)}25%{transform:scale(1.2)}45%{transform:scale(0.9)}65%{transform:scale(1.1)}85%{transform:scale(0.97)}}
        @keyframes jayBreathe{0%,100%{transform:scale(1) translateY(0)}50%{transform:scale(1.03) translateY(-2px)}}
        @keyframes jayStretch{0%,100%{transform:scaleY(1) scaleX(1)}20%{transform:scaleY(1.18) scaleX(0.88)}50%{transform:scaleY(0.9) scaleX(1.1)}80%{transform:scaleY(1.05) scaleX(0.96)}}
        @keyframes jayLook{0%,100%{transform:translateX(0) rotate(0)}12%{transform:translateX(-6px) rotate(-8deg)}30%{transform:translateX(-2px) rotate(-2deg)}50%{transform:translateX(6px) rotate(8deg)}70%{transform:translateX(2px) rotate(2deg)}}
        @keyframes jayChirp{0%,100%{transform:translateY(0) scale(1)}15%{transform:translateY(-5px) scale(1.08)}30%{transform:translateY(-1px) scale(1.02)}50%{transform:translateY(-5px) scale(1.08)}70%{transform:translateY(-1px) scale(1.01)}}
        @keyframes jayNap{0%{transform:translateY(0) rotate(0)}12%{transform:translateY(4px) rotate(5deg)}88%{transform:translateY(4px) rotate(5deg)}100%{transform:translateY(0) rotate(0)}}
        @keyframes jayTail{0%,100%{transform:rotate(0)}12%{transform:rotate(-10deg)}35%{transform:rotate(10deg)}58%{transform:rotate(-7deg)}80%{transform:rotate(5deg)}}
        @keyframes jayBlink{0%,20%,80%,100%{transform:scaleY(1)}50%{transform:scaleY(0.75)}}
        @keyframes thoughtPop{0%{opacity:0;transform:scale(0.6) translateY(8px)}40%{transform:scale(1.08) translateY(-2px)}100%{opacity:1;transform:scale(1) translateY(0)}}
      `}</style>
    </>
  );
}
